import {apiClient} from "@/lib/api/client"
import type {DestructionActsPageResult, DestructionActStatus, OtkDestructionAct, Sort} from "shared"

export type { DestructionActsPageResult, OtkDestructionAct } from "shared"

// otk-destruction-acts.ts's `GET /otk/destruction-acts` — cursor-paginated,
// subject-scoped server-side, filterable by `status` (the backend defaults to
// `ACCEPTED`) and `actDate` range. Same "one page per call, explicit
// load-more" shape `getOtkBlanksPage`/`getOtkDocumentsPage` already use.
export async function getDestructionActsPage({
  status,
  actDateFrom,
  actDateTo,
  cursor,
  pageSize = 25,
  sort = "DESC",
}: {
  status?: DestructionActStatus
  actDateFrom?: string
  actDateTo?: string
  cursor?: string
  pageSize?: number
  sort?: Sort
}): Promise<DestructionActsPageResult> {
  const { data } = await apiClient.get<DestructionActsPageResult>(
    "/otk/destruction-acts",
    { params: { status, actDateFrom, actDateTo, cursor, pageSize, sort } }
  )
  return data
}

// otk-destruction-acts.ts's `GET /otk/destruction-acts/:id` — backs
// `ActViewDialog`'s on-demand open (warehouse.md's "Акт Знищення" column and
// warehouse-acts.md's archive both resolve an act this way, not from a
// locally cached list).
export async function getDestructionActById(id: string): Promise<OtkDestructionAct> {
  const { data } = await apiClient.get<OtkDestructionAct>(`/otk/destruction-acts/${id}`)
  return data
}

// Send the same serialized JSON that was signed, without Axios reformatting it.
export async function submitDestructionAct(
  signedPayload: string,
  kepSignature: string
): Promise<OtkDestructionAct> {
  const { data } = await apiClient.post<OtkDestructionAct>("/otk/destruction-acts/sign", signedPayload, {
    transformRequest: [(body: string) => body],
    headers: {
      "Content-Type": "application/json",
      "X-KEP-Signature": kepSignature,
    },
  })
  return data
}

// otk-destruction-acts.ts's `POST /otk/destruction-acts/:id/retry` — re-sends
// the stored signed JSON and КЕП of a `SUBMISSION_PENDING` act; no new
// signature. Answers `425 SUBMISSION_NOT_READY` before `submitRetryAt` and
// `502 SUBMISSION_FAILED` when the registry fails again, both with the
// current `act` in the body (see `pendingActFromError`).
export async function retryDestructionAct(id: string): Promise<OtkDestructionAct> {
  const { data } = await apiClient.post<OtkDestructionAct>(`/otk/destruction-acts/${id}/retry`)
  return data
}
